import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Navigation, RefreshCw, Truck, Package, CheckCircle, DollarSign,
  MapPin, User, Radio, WifiOff, ChevronDown, Send, AlertCircle,
  Store, Phone, ChevronRight, Bell, BellOff, Zap, Clock,
} from "lucide-react";
import { io } from "socket.io-client";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import RyderProfileModal from "../../components/RyderProfileModal";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

// Alerta suave: nuevo pedido entrante
function playAlert() {
  try {
    navigator.vibrate?.([150, 80, 150]);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume().then(() => {
      [[880, 0, 0.18], [660, 0.2, 0.25], [880, 0.48, 0.18]].forEach(([freq, start, dur]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gain.gain.setValueAtTime(0.35, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.05);
      });
    });
  } catch (_) {}
}

// Alarma urgente: pedidos sin atender — triple tono más fuerte
function playUrgentAlarm() {
  try {
    navigator.vibrate?.([200, 100, 200, 100, 200]);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume().then(() => {
      const sequence = [
        [1046, 0, 0.12], [1175, 0.15, 0.12], [1046, 0.30, 0.12],
        [1175, 0.45, 0.12], [1046, 0.60, 0.20],
      ];
      sequence.forEach(([freq, start, dur]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "square";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gain.gain.setValueAtTime(0.18, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.05);
      });
    });
  } catch (_) {}
}

const REFRESH_INTERVAL_MS = 8000; // 8 segundos

dayjs.extend(relativeTime);
dayjs.locale("es");

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CFG = {
  confirmado: {
    label: "Sin domiciliario",
    strip: "bg-amber-400",
    badge: "bg-amber-100 text-amber-700",
    dot: "bg-amber-400",
    ryderBg: "",
  },
  asignado: {
    label: "Domiciliario asignado",
    strip: "bg-violet-500",
    badge: "bg-violet-100 text-violet-700",
    dot: "bg-violet-500",
    ryderBg: "bg-violet-50 border-violet-100",
  },
  listo: {
    label: "¡Listo para retirar!",
    strip: "bg-emerald-400",
    badge: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-400",
    ryderBg: "bg-emerald-50 border-emerald-100",
  },
  en_camino: {
    label: "En camino",
    strip: "bg-blue-500",
    badge: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
    ryderBg: "bg-blue-50 border-blue-100",
  },
};

const RYDER_CFG = {
  disponible: { label: "Libre — puede tomar pedidos", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700", row: "bg-white border-gray-200" },
  ocupado:    { label: "Ocupado — en un pedido",       dot: "bg-amber-400",  badge: "bg-amber-100 text-amber-700",    row: "bg-white border-gray-200" },
  offline:    { label: "Desconectado",                  dot: "bg-gray-300",   badge: "bg-gray-100 text-gray-500",      row: "bg-gray-50 border-gray-100 opacity-50" },
};

// ── AssignDropdown ────────────────────────────────────────────────────────────
function AssignDropdown({ order, ryders, onAssign, assigning }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const sorted = [
    ...ryders.filter(r => r.status === "disponible"),
    ...ryders.filter(r => r.status === "ocupado"),
    ...ryders.filter(r => r.status === "offline"),
  ];
  const available = ryders.filter(r => r.status === "disponible").length;
  const isAssigning = assigning === order.id;
  const hasRyder    = !!order.ryderId;

  if (order.status === "en_camino") return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !isAssigning && setOpen(o => !o)}
        disabled={isAssigning}
        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition border disabled:opacity-60 ${
          hasRyder
            ? "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
            : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
        }`}
      >
        {isAssigning
          ? <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> Asignando...</>
          : hasRyder
          ? <><User size={12} /> Reasignar <ChevronDown size={11} /></>
          : <><Send size={12} /> Asignar domiciliario <ChevronDown size={11} /></>
        }
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
          >
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-600">
                {available > 0
                  ? `${available} libre${available > 1 ? "s" : ""} · ${ryders.length} en total`
                  : `${ryders.length} domiciliario${ryders.length !== 1 ? "s" : ""} en tu empresa`}
              </p>
            </div>
            {sorted.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                <WifiOff size={18} className="mx-auto mb-1 text-gray-300" />
                No tienes domiciliarios disponibles
              </div>
            ) : (
              <div className="max-h-52 overflow-y-auto">
                {sorted.map(r => {
                  const name = r.name || r.user?.name || "Sin nombre";
                  const st   = RYDER_CFG[r.status] ?? RYDER_CFG.offline;
                  return (
                    <button
                      key={r.id}
                      onClick={() => { onAssign(order.id, r.id); setOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-indigo-50 text-left transition group"
                    >
                      <div className="relative shrink-0">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-[10px] font-bold">
                          {name.slice(0, 2).toUpperCase()}
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-white ${st.dot}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-indigo-700">{name}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st.badge}`}>{st.label}</span>
                      </div>
                      <Send size={12} className="text-gray-300 group-hover:text-indigo-500 shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── AvailableOrderCard ────────────────────────────────────────────────────────
function AvailableOrderCard({ order, claiming, onClaim }) {
  const age        = dayjs(order.confirmedAt ?? order.createdAt).fromNow();
  const isClaiming = claiming === order.id;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -6 }}
      className="bg-white rounded-xl border border-orange-200 overflow-hidden flex"
    >
      {/* Status strip */}
      <div className="w-1 shrink-0 bg-orange-400" />

      <div className="flex-1 p-3.5 space-y-2.5">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-black font-mono text-gray-700">#{order.orderNumber}</span>
            <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
              <Clock size={8} /> {age}
            </span>
          </div>
          <span className="text-xs font-black text-gray-900">
            ${Number(order.deliveryFee ?? 0).toLocaleString()}
            <span className="text-[9px] font-normal text-gray-400 ml-0.5">fee</span>
          </span>
        </div>

        {/* Store → customer */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs">
            <Store size={10} className="text-gray-400 shrink-0" />
            <span className="font-semibold text-gray-800 truncate">{order.company?.name ?? "Comercio"}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <User size={10} className="shrink-0" />
            <span className="truncate">{order.user?.name ?? "Cliente"}</span>
          </div>
          {order.deliveryAddress && (
            <div className="flex items-start gap-1.5 text-[10px] text-gray-400">
              <MapPin size={9} className="shrink-0 mt-0.5" />
              <span className="line-clamp-1">{order.deliveryAddress}</span>
            </div>
          )}
        </div>

        {/* Items */}
        {order.items?.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {order.items.slice(0, 2).map((item, i) => (
              <span key={i} className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                {item.qty ?? item.quantity}× {item.product?.title ?? "ítem"}
              </span>
            ))}
            {order.items.length > 2 && (
              <span className="text-[9px] text-gray-400">+{order.items.length - 2} más</span>
            )}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={() => !isClaiming && onClaim(order.id)}
          disabled={isClaiming}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition disabled:opacity-60"
        >
          {isClaiming
            ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Asignando...</>
            : <><Zap size={10} /> ¡Tomar este pedido!</>
          }
        </button>
      </div>
    </motion.div>
  );
}

// ── ActiveOrderCard ───────────────────────────────────────────────────────────
function ActiveOrderCard({ order, ryders, onAssign, assigning, onViewRyder }) {
  const cfg     = STATUS_CFG[order.status] ?? STATUS_CFG.confirmado;
  const ageMin  = dayjs().diff(dayjs(order.createdAt), "minute");
  const isUrgent = order.status === "confirmado" && ageMin > 15;
  const ryder   = order.ryder;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className="bg-white rounded-xl border border-gray-100 shadow-sm flex"
    >
      {/* Status strip */}
      <div className={`w-1 shrink-0 rounded-l-xl ${isUrgent ? "bg-red-400" : cfg.strip}`} />

      <div className="flex-1 min-w-0">
        {/* Urgent banner — full width, NOT inside overflow-hidden parent */}
        {isUrgent && (
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-red-600 bg-red-50 px-3 py-1.5 border-b border-red-100 rounded-tr-xl">
            <AlertCircle size={10} /> Sin domiciliario hace {ageMin} min — ¡asigna uno ya!
          </div>
        )}
        {order.status === "listo" && (
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 border-b border-emerald-100 rounded-tr-xl animate-pulse">
            <CheckCircle size={10} /> ✅ El restaurante ya tiene listo el pedido — el domiciliario va en camino
          </div>
        )}
        <div className="p-3.5 space-y-2.5">

        {/* Row 1: status + order number + age + fee */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${["en_camino","listo"].includes(order.status) ? "animate-pulse" : ""}`} />
              {cfg.label}
            </span>
            <span className="text-[10px] font-mono font-black text-gray-400">#{order.orderNumber}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-black text-gray-900">
              ${Number(order.deliveryFee ?? 0).toLocaleString()}
            </span>
            <span className="text-[9px] text-gray-400">{dayjs(order.createdAt).fromNow()}</span>
          </div>
        </div>

        {/* Row 2: store + customer inline */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div>
            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Tienda</p>
            <div className="flex items-center gap-1 text-xs">
              <Store size={10} className="text-gray-400 shrink-0" />
              <span className="font-semibold text-gray-800 truncate">{order.company?.name ?? "—"}</span>
            </div>
          </div>
          <div>
            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Cliente</p>
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <User size={10} className="shrink-0" />
              <span className="truncate">{order.user?.name ?? "—"}</span>
              {order.user?.phone && (
                <a
                  href={`tel:${order.user.phoneCountryCode ?? ""}${order.user.phone}`}
                  className="ml-auto shrink-0 text-indigo-500 hover:text-indigo-700"
                  onClick={e => e.stopPropagation()}
                >
                  <Phone size={9} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Row 3: address */}
        {order.deliveryAddress && (
          <div className="flex items-start gap-1.5 text-[10px] text-gray-400">
            <MapPin size={9} className="shrink-0 mt-0.5" />
            <span className="line-clamp-1">{order.deliveryAddress}</span>
          </div>
        )}

        {/* Row 4: Ryder block (if assigned) */}
        {ryder && (
          <div className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg border ${cfg.ryderBg}`}>
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-[9px] font-black shrink-0">
              {(ryder.user?.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold truncate ${order.status === "en_camino" ? "text-emerald-800" : "text-violet-800"}`}>
                {ryder.user?.name}
              </p>
              <p className={`text-[9px] font-semibold ${order.status === "en_camino" ? "text-blue-600" : order.status === "listo" ? "text-emerald-600" : "text-violet-500"}`}>
                {order.status === "en_camino" ? "🛵 En ruta ahora" : order.status === "listo" ? "✅ Pedido listo — va a recoger" : "Asignado · esperando que el restaurante aliste"}
              </p>
            </div>
            {["listo", "en_camino"].includes(order.status) && (
              <button
                onClick={() => onViewRyder?.(ryder)}
                className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 text-white rounded-lg transition shrink-0 ${order.status === "listo" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"}`}
              >
                <MapPin size={9} /> Ver ubicación
              </button>
            )}
          </div>
        )}

        {/* Row 5: action footer */}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-50">
          {order.items?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {order.items.slice(0, 2).map((item, i) => (
                <span key={i} className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                  {item.qty ?? item.quantity}× {item.product?.title ?? "ítem"}
                </span>
              ))}
              {order.items.length > 2 && (
                <span className="text-[9px] text-gray-400">+{order.items.length - 2}</span>
              )}
            </div>
          )}
          <div className="ml-auto shrink-0">
            <AssignDropdown order={order} ryders={ryders} onAssign={onAssign} assigning={assigning} />
          </div>
        </div>
        </div>{/* end p-3.5 */}
      </div>
    </motion.div>
  );
}

// ── FleetRyderRow ─────────────────────────────────────────────────────────────
function FleetRyderRow({ ryder, orders, onView }) {
  const cfg  = RYDER_CFG[ryder.status] ?? RYDER_CFG.offline;
  const name = ryder.user?.name || "Sin nombre";
  const active = orders.find(o =>
    o.ryderId === ryder.id && ["asignado", "listo", "en_camino"].includes(o.status)
  );

  return (
    <button
      type="button"
      onClick={() => onView?.(ryder)}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition text-left group ${cfg.row} hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow-sm`}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-[11px] font-bold">
          {name.slice(0, 2).toUpperCase()}
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${cfg.dot}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-gray-900 truncate group-hover:text-indigo-700 transition-colors">{name}</p>
        {active ? (
          <p className="text-[9px] text-gray-500 truncate">
            <span className={active.status === "en_camino" ? "text-blue-600 font-semibold" : active.status === "listo" ? "text-emerald-600 font-semibold" : "text-violet-600 font-semibold"}>
              {active.status === "en_camino" ? "En ruta" : active.status === "listo" ? "Recogiendo" : "Asignado"}
            </span>
            {" ·"} {active.company?.name ?? `#${active.orderNumber}`}
          </p>
        ) : (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
        )}
      </div>

      {/* Stats + chevron */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <p className="text-xs font-black text-gray-800">{ryder.deliveriesToday ?? 0}</p>
          <p className="text-[8px] text-gray-400 leading-none">hoy</p>
        </div>
        <ChevronRight size={12} className="text-gray-300 group-hover:text-indigo-400 transition-colors" />
      </div>
    </button>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gray-200" />
          <div className="space-y-1.5">
            <div className="w-40 h-4 bg-gray-200 rounded" />
            <div className="w-24 h-2.5 bg-gray-100 rounded" />
          </div>
        </div>
        <div className="w-28 h-8 bg-gray-200 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Array(5).fill(0).map((_, i) => <div key={i} className="h-20 bg-gray-200 rounded-2xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          {Array(4).fill(0).map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-xl" />)}
        </div>
        <div className="space-y-2">
          {Array(5).fill(0).map((_, i) => <div key={i} className="h-14 bg-gray-200 rounded-xl" />)}
        </div>
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ dot, label, count, countColor = "bg-indigo-100 text-indigo-700" }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <h2 className="text-[11px] font-black text-gray-700 uppercase tracking-wider whitespace-nowrap">{label}</h2>
      {count != null && count > 0 && (
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${countColor}`}>{count}</span>
      )}
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// ── HelpModal ─────────────────────────────────────────────────────────────────
const HELP_STEPS = [
  {
    emoji: "🟠",
    title: "Llega un pedido disponible",
    desc: "Cuando un restaurante confirma un pedido, aparece en la sección naranja de arriba. Suena una alarma para avisarte.",
    tip: "¡Tómalo rápido antes de que otra empresa lo tome!",
    tipColor: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    emoji: "⚡",
    title: "Toma el pedido",
    desc: 'Presiona el botón naranja "¡Tomar este pedido!" para asignarlo a tu empresa. El pedido aparecerá en tu lista.',
    tip: null,
    tipColor: "",
  },
  {
    emoji: "🏍️",
    title: "Asigna un domiciliario",
    desc: 'Dentro del pedido, presiona "Asignar domiciliario" y escoge quién va a recogerlo. Solo aparecen los que están libres primero.',
    tip: "Un punto verde = disponible · Amarillo = en otro pedido · Gris = desconectado",
    tipColor: "bg-gray-50 text-gray-600 border-gray-200",
  },
  {
    emoji: "✅",
    title: "El restaurante alista el pedido",
    desc: "Cuando el restaurante marca el pedido como listo, la tarjeta se pone verde y el domiciliario recibe una alerta para ir a recogerlo.",
    tip: null,
    tipColor: "",
  },
  {
    emoji: "🛵",
    title: "El domiciliario va en camino",
    desc: "El domiciliario recoge el pedido y marca que está en camino. Puedes ver su ubicación en tiempo real tocando el botón azul.",
    tip: null,
    tipColor: "",
  },
  {
    emoji: "🎉",
    title: "Pedido entregado",
    desc: "El domiciliario confirma la entrega. El pedido sale de la lista y suma a tus ganancias del día.",
    tip: null,
    tipColor: "",
  },
];

const HELP_SECTIONS = [
  {
    icon: "🟠",
    title: "Pedidos disponibles para tomar",
    desc: "Pedidos que aún no tienen empresa de domicilio asignada. Cualquier empresa puede tomarlos.",
  },
  {
    icon: "📋",
    title: "Pedidos de tu empresa",
    desc: "Los pedidos que ya son tuyos. Aquí los gestionas: asignas domiciliarios, ves el estado y el progreso.",
  },
  {
    icon: "🏍️",
    title: "Mis domiciliarios",
    desc: "Tu equipo de domiciliarios. Verde = libre para un pedido. Amarillo = ya tiene uno. Gris = está apagado.",
  },
];

function HelpModal({ onClose }) {
  const [tab, setTab] = useState("flujo");
  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.2 }}
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-black text-gray-900">¿Cómo funciona el panel?</h2>
            <p className="text-xs text-gray-400 mt-0.5">Guía rápida para gestionar pedidos y domiciliarios</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition text-gray-500 text-lg font-bold"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 shrink-0">
          {[
            { key: "flujo", label: "📦 Paso a paso" },
            { key: "secciones", label: "🗂️ El panel" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition ${tab === t.key ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {tab === "flujo" ? (
            <>
              <p className="text-xs text-gray-400 mb-1">Así va un pedido desde que llega hasta que se entrega:</p>
              {HELP_STEPS.map((step, i) => (
                <div key={i} className="flex gap-3">
                  {/* Timeline */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-base">{step.emoji}</div>
                    {i < HELP_STEPS.length - 1 && <div className="w-0.5 flex-1 bg-gray-100 mt-1 mb-0" style={{ minHeight: 12 }} />}
                  </div>
                  {/* Text */}
                  <div className="pb-3">
                    <p className="text-xs font-black text-gray-800 mb-0.5">{i + 1}. {step.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                    {step.tip && (
                      <div className={`mt-1.5 text-[10px] font-semibold px-2 py-1 rounded-lg border ${step.tipColor}`}>
                        💡 {step.tip}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400 mb-1">El panel tiene tres secciones principales:</p>
              {HELP_SECTIONS.map((s, i) => (
                <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-xl shrink-0">{s.icon}</span>
                  <div>
                    <p className="text-xs font-black text-gray-800 mb-0.5">{s.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}

              {/* Status colors guide */}
              <div className="mt-2 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                <p className="text-[10px] font-black text-indigo-700 uppercase tracking-widest mb-2">Colores del pedido</p>
                <div className="space-y-1.5">
                  {[
                    { color: "bg-amber-400", label: "Amarillo", desc: "Sin domiciliario — asigna uno" },
                    { color: "bg-violet-500", label: "Morado", desc: "Domiciliario asignado — espera que el restaurante lo aliste" },
                    { color: "bg-emerald-400", label: "Verde", desc: "¡Listo! El domiciliario ya va a recogerlo" },
                    { color: "bg-blue-500",    label: "Azul",  desc: "En camino — ya fue recogido y va al cliente" },
                  ].map(c => (
                    <div key={c.label} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.color}`} />
                      <span className="font-semibold">{c.label}:</span>
                      <span>{c.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="p-3 bg-orange-50 rounded-xl border border-orange-100">
                <p className="text-[10px] font-black text-orange-700 uppercase tracking-widest mb-2">Consejos</p>
                <ul className="space-y-1 text-xs text-orange-800">
                  <li>🔔 Activa el sonido para que la alarma funcione cuando llegue un pedido</li>
                  <li>📱 El panel se actualiza automáticamente cada 8 segundos</li>
                  <li>⚡ Toma los pedidos disponibles rápido — otros pueden tomarlos antes</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition"
          >
            ¡Entendido!
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DeliveryDispatch() {
  const [company,         setCompany]         = useState(null);
  const [orders,          setOrders]          = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [ryders,          setRyders]          = useState([]);
  const [stats,           setStats]           = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [refreshing,      setRefreshing]      = useState(false);
  const [assigning,       setAssigning]       = useState(null);
  const [claiming,        setClaiming]        = useState(null);
  const [statusFilter,    setStatusFilter]    = useState("all");
  const [profileRyder,    setProfileRyder]    = useState(null);
  const [newOrderBadge,   setNewOrderBadge]   = useState(0);
  const [connected,       setConnected]       = useState(false);
  const [muted,           setMuted]           = useState(() => localStorage.getItem("dispatch_muted") === "1");
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [showHelp,        setShowHelp]        = useState(false);
  const lastFetchRef         = useRef(null);
  const companyRef           = useRef(null);
  const prevAvailableCount   = useRef(null);   // detectar nuevos pedidos via polling
  const mutedRef             = useRef(muted);
  const socketRef            = useRef(null);   // referencia al socket para re-suscribir tras race condition
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const fetchData = useCallback(async (companyId, silent = false) => {
    if (!silent) setLoading(true);
    else         setRefreshing(true);
    try {
      const r = await API.get(`/delivery-companies/${companyId}/queue`);
      const d = r.data?.data ?? r.data;
      console.log('[Despacho] Queue response para empresa', companyId, '→ orders:', (d.orders ?? []).length, 'disponibles:', (d.availableOrders ?? []).length, 'raw:', d);
      setOrders(Array.isArray(d.orders) ? d.orders : []);
      setAvailableOrders(Array.isArray(d.availableOrders) ? d.availableOrders : []);
      setRyders(Array.isArray(d.ryders) ? d.ryders : []);
      setStats(d.stats ?? null);
      lastFetchRef.current = new Date();
    } catch (err) {
      console.error('[Despacho] fetchData error (empresa:', companyId, '):', err?.response?.status, err?.response?.data ?? err?.message);
      if (!silent) toast.error("Error al cargar la cola de despacho");
    }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    API.get("/delivery-companies/me")
      .then(r => {
        const comp = r.data?.data?.company ?? r.data?.company;
        setCompany(comp);
        if (comp?.id) fetchData(comp.id, false);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [fetchData]);

  // Polling + barra de progreso del próximo refresh
  useEffect(() => {
    if (!company?.id) return;
    setRefreshProgress(0);
    const start  = Date.now();
    const ticker = setInterval(() => {
      const elapsed = Date.now() - start;
      setRefreshProgress(Math.min(100, (elapsed / REFRESH_INTERVAL_MS) * 100));
    }, 250);
    const iv = setInterval(() => {
      fetchData(company.id, true);
      setRefreshProgress(0);
      // Re-suscribir en cada poll por si el socket reconectó con token vencido
      const sock = socketRef.current;
      if (sock?.connected) {
        sock.emit("delivery:subscribe", company.id);
      }
    }, REFRESH_INTERVAL_MS);
    return () => { clearInterval(iv); clearInterval(ticker); };
  }, [company?.id, fetchData]);

  useEffect(() => { companyRef.current = company; }, [company]);

  // Fix race condition: si el socket ya estaba conectado cuando cargó el company,
  // re-emitir delivery:subscribe ahora que tenemos el ID
  useEffect(() => {
    if (!company?.id) return;
    const socket = socketRef.current;
    console.log('[Despacho] company cargado id=', company.id, '| socket conectado:', socket?.connected);
    if (socket?.connected) {
      console.log('[Despacho] Re-emitiendo delivery:subscribe tras carga de company');
      socket.emit("delivery:subscribe", company.id);
    }
  }, [company?.id]); // eslint-disable-line

  // Guardar preferencia mute en localStorage
  useEffect(() => {
    localStorage.setItem("dispatch_muted", muted ? "1" : "0");
  }, [muted]);

  // Detectar nuevos pedidos disponibles incluso desde polling (no solo socket)
  useEffect(() => {
    const cur = availableOrders.length;
    if (prevAvailableCount.current === null) {
      prevAvailableCount.current = cur;
      return;
    }
    if (cur > prevAvailableCount.current && !mutedRef.current) {
      playAlert();
      toast.warning(
        <div className="flex items-start gap-2">
          <Zap size={16} className="shrink-0 mt-0.5 text-orange-500" />
          <div>
            <p className="font-bold text-sm">
              {cur - prevAvailableCount.current === 1
                ? "Nuevo pedido disponible"
                : `${cur - prevAvailableCount.current} pedidos nuevos disponibles`}
            </p>
            <p className="text-xs text-gray-500">Sin empresa asignada — tómalos ahora</p>
          </div>
        </div>,
        { autoClose: 7000, icon: false }
      );
    }
    prevAvailableCount.current = cur;
  }, [availableOrders.length]); // eslint-disable-line

  // Alarma repetida cada 30s mientras haya pedidos disponibles sin tomar
  useEffect(() => {
    if (availableOrders.length === 0) return;
    const t = setInterval(() => {
      if (!mutedRef.current) playUrgentAlarm();
    }, 30000);
    return () => clearInterval(t);
  }, [availableOrders.length > 0]); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ["websocket"], auth: { token } });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      // Suscribirse solo si el company ya cargó; si no, el useEffect de company?.id lo hace
      const cid = companyRef.current?.id;
      console.log('[Despacho] Socket conectado. company.id =', cid ?? '(no cargado aún)');
      if (cid) {
        console.log('[Despacho] Emitiendo delivery:subscribe para empresa', cid);
        socket.emit("delivery:subscribe", cid);
      }
    });
    socket.on("disconnect", () => setConnected(false));

    // Nuevo pedido libre (sin empresa asignada) — cualquier delivery puede tomarlo
    socket.on("order:available", (order) => {
      console.log('[Despacho] Socket: order:available recibido', order?.orderNumber, 'deliveryCompanyId:', order?.deliveryCompanyId);
      if (!mutedRef.current) playAlert();
      const companyName  = order?.company?.name ?? "un comercio";
      const num          = order?.orderNumber ? `#${order.orderNumber}` : "";
      const isUnassigned = !order?.deliveryCompanyId;

      if (isUnassigned) {
        // Pedido libre: notificar con alerta naranja
        toast.warning(
          <div className="flex items-start gap-2">
            <Zap size={16} className="shrink-0 mt-0.5 text-orange-500" />
            <div>
              <p className="font-bold text-sm">¡Pedido libre! {num}</p>
              <p className="text-xs text-gray-500">De {companyName} — disponible para tomar</p>
            </div>
          </div>,
          { autoClose: 8000, icon: false }
        );
      } else {
        // Pedido asignado a ESTA empresa por el restaurante
        toast.info(
          <div className="flex items-start gap-2">
            <Bell size={16} className="shrink-0 mt-0.5 text-indigo-500" />
            <div>
              <p className="font-bold text-sm">Pedido asignado {num}</p>
              <p className="text-xs text-gray-500">De {companyName} — asignado a tu empresa</p>
            </div>
          </div>,
          { autoClose: 8000, icon: false }
        );
      }
      setNewOrderBadge(n => n + 1);
      const cid = companyRef.current?.id;
      if (cid) fetchData(cid, true);
    });

    // Otro panel tomó un pedido libre → removerlo de disponibles en tiempo real
    socket.on("order:claimed", ({ orderId }) => {
      setAvailableOrders(prev => prev.filter(o => o.id !== orderId));
    });

    // El restaurante marcó el pedido como listo para retirar → refrescar cola
    socket.on("order:ready-for-pickup", () => {
      const cid = companyRef.current?.id;
      if (cid) fetchData(cid, true);
    });

    // Cambio de estado del pedido (domiciliario marca en camino, entregado, etc.)
    socket.on("order:status", () => {
      const cid = companyRef.current?.id;
      if (cid) fetchData(cid, true);
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []); // eslint-disable-line

  const handleAssign = async (orderId, ryderId) => {
    setAssigning(orderId);
    try {
      await API.patch(`/orders/${orderId}/assign-ryder`, { ryderId });
      toast.success("Ryder asignado al pedido");
      if (company?.id) await fetchData(company.id, true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al asignar ryder");
    } finally { setAssigning(null); }
  };

  const handleClaim = async (orderId) => {
    if (!company?.id) return;
    setClaiming(orderId);
    // Actualización optimista: remover de disponibles al instante
    setAvailableOrders(prev => prev.filter(o => o.id !== orderId));
    try {
      await API.patch(`/delivery-companies/${company.id}/orders/${orderId}/claim`);
      toast.success("¡Pedido tomado! Asigna un ryder desde tu cola");
      await fetchData(company.id, true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al tomar el pedido");
      // Revertir si falló (recargar datos reales)
      await fetchData(company.id, true);
    } finally { setClaiming(null); }
  };

  if (loading) return <Skeleton />;

  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
          <Truck size={28} className="text-indigo-500" />
        </div>
        <h2 className="text-xl font-black text-gray-800 mb-2">No tienes empresa registrada</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          Para usar el panel de despacho necesitas registrar tu empresa de domicilio.
          Ve a la sección de <strong>Configuración</strong> para crear tu empresa.
        </p>
        <p className="text-xs text-red-400 mt-4 font-mono bg-red-50 px-3 py-1.5 rounded-lg">
          GET /delivery-companies/me → company: null
        </p>
      </div>
    );
  }

  const disponibles = ryders.filter(r => r.status === "disponible").length;
  const ocupados    = ryders.filter(r => r.status === "ocupado").length;
  const offline     = ryders.filter(r => r.status === "offline").length;
  const filteredOrders = statusFilter === "all"
    ? orders
    : orders.filter(o => o.status === statusFilter);

  const FILTER_TABS = [
    { key: "all",        label: "Todos",             count: orders.length },
    { key: "confirmado", label: "Sin domiciliario",  count: stats?.confirmado ?? 0 },
    { key: "asignado",   label: "Asignados",         count: stats?.asignado   ?? 0 },
    { key: "listo",      label: "¡Listos!",          count: stats?.listo      ?? 0 },
    { key: "en_camino",  label: "En ruta",           count: stats?.en_camino  ?? 0 },
  ];

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
              <Navigation size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-black text-gray-900 leading-tight">Centro de Despacho</h1>
              <p className="text-[11px] text-gray-500 leading-tight">{company?.name ?? "—"}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Connection status */}
            <div className={`hidden sm:flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${
              connected
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-gray-50 text-gray-400 border-gray-200"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`} />
              {connected ? "En vivo" : "Reconectando..."}
            </div>

            {/* Mute toggle */}
            <button
              onClick={() => setMuted(m => !m)}
              title={muted ? "Activar alarma" : "Silenciar alarma"}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-full border transition ${
                muted
                  ? "bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200"
                  : availableOrders.length > 0
                    ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {muted
                ? <><BellOff size={12} /> Silenciado</>
                : <><Bell size={12} className={availableOrders.length > 0 ? "animate-bounce" : ""} /> Alarma activa</>
              }
            </button>

            {/* Help button */}
            <button
              onClick={() => setShowHelp(true)}
              title="¿Cómo funciona?"
              className="w-9 h-9 flex items-center justify-center text-sm font-black bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition shadow-sm"
            >
              ?
            </button>

            {/* Refresh button */}
            <button
              onClick={() => { company?.id && fetchData(company.id, true); setNewOrderBadge(0); setRefreshProgress(0); }}
              disabled={refreshing}
              className="relative flex items-center gap-2 px-3.5 py-2 text-xs font-semibold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition disabled:opacity-50 shadow-sm"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Actualizar</span>
              {newOrderBadge > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 animate-bounce">
                  {newOrderBadge}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Auto-refresh progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-0.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-400 rounded-full transition-all duration-300"
              style={{ width: `${refreshProgress}%` }}
            />
          </div>
          {lastFetchRef.current && (
            <span className="text-[9px] text-gray-400 whitespace-nowrap shrink-0 flex items-center gap-1">
              <span className={`w-1 h-1 rounded-full ${refreshing ? "bg-indigo-400 animate-pulse" : "bg-gray-300"}`} />
              {refreshing ? "Actualizando..." : "Se actualiza solo"}
            </span>
          )}
        </div>
      </div>

      {/* ── Stats strip ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        {[
          {
            icon: Zap,
            label: "Para tomar",
            value: stats?.disponibles ?? availableOrders.length,
            accent: "bg-orange-500",
            urgent: (stats?.disponibles ?? availableOrders.length) > 0,
            urgentRing: "ring-1 ring-orange-200 shadow-orange-100 shadow",
          },
          {
            icon: Package,
            label: "Sin domiciliario",
            value: stats?.confirmado ?? 0,
            accent: "bg-amber-500",
            urgent: (stats?.confirmado ?? 0) > 0,
            urgentRing: "ring-1 ring-amber-200",
          },
          { icon: Radio,       label: "En ruta",       value: stats?.en_camino    ?? 0, accent: "bg-blue-500"    },
          { icon: CheckCircle, label: "Entregas hoy",  value: stats?.entregadoHoy ?? 0, accent: "bg-emerald-500" },
          {
            icon: DollarSign,
            label: "Ganancias hoy",
            value: `$${Number(stats?.revenueHoy ?? 0).toLocaleString()}`,
            accent: "bg-teal-500",
          },
        ].map(({ icon: Icon, label, value, accent, urgent, urgentRing }) => (
          <div
            key={label}
            className={`bg-white rounded-2xl p-3.5 border border-gray-100 flex items-center gap-2.5 ${urgent ? urgentRing : ""}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
              <Icon size={16} className="text-white" />
            </div>
            <div>
              <p className={`text-lg font-black leading-none ${urgent ? "text-gray-900" : "text-gray-900"}`}>{value}</p>
              <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Main layout ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

        {/* ── Operations column (2/3) ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Section A: Pedidos libres — sin empresa asignada, cualquiera puede tomarlos */}
          {availableOrders.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full shrink-0 bg-orange-400 animate-pulse" />
                <h2 className="text-[11px] font-black text-gray-700 uppercase tracking-wider">
                  Pedidos disponibles para tomar
                </h2>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                  {availableOrders.length}
                </span>
                <div className="flex-1 h-px bg-orange-100" />
              </div>
              <p className="text-[10px] text-gray-400 mb-2.5">
                Pedidos confirmados por el restaurante, listos para que tu empresa los atienda
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <AnimatePresence mode="popLayout">
                  {availableOrders.map(order => (
                    <AvailableOrderCard
                      key={order.id}
                      order={order}
                      claiming={claiming}
                      onClaim={handleClaim}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* Section B: Cola propia — pedidos asignados a esta empresa */}
          <section>
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0 bg-indigo-600" />
                <h2 className="text-[11px] font-black text-gray-700 uppercase tracking-wider">
                  Pedidos de tu empresa
                </h2>
                {orders.length > 0 && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                    {orders.length}
                  </span>
                )}
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {FILTER_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setStatusFilter(tab.key)}
                    className={`flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full transition ${
                      statusFilter === tab.key
                        ? "bg-gray-900 text-white"
                        : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`text-[8px] font-bold min-w-[14px] h-3.5 flex items-center justify-center px-1 rounded-full ${
                        statusFilter === tab.key ? "bg-white/20" : "bg-gray-100 text-gray-500"
                      }`}>{tab.count}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mb-2.5">
              Pedidos activos de tu empresa — asigna un domiciliario a cada uno para enviarlo
            </p>

            {filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 bg-white rounded-2xl border border-gray-100 text-center">
                <Package size={36} className="mb-2.5 text-gray-200" />
                <p className="font-bold text-gray-400 text-sm">
                  {orders.length === 0 ? "Todo tranquilo por ahora" : "Sin pedidos en este estado"}
                </p>
                <p className="text-xs text-gray-300 mt-1">
                  {orders.length === 0 && availableOrders.length > 0
                    ? "Toma un pedido disponible arriba para empezar"
                    : "Cuando lleguen nuevos pedidos aparecerán aquí"}
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                <AnimatePresence mode="popLayout">
                  {filteredOrders.map(order => (
                    <ActiveOrderCard
                      key={order.id}
                      order={order}
                      ryders={ryders}
                      onAssign={handleAssign}
                      assigning={assigning}
                      onViewRyder={setProfileRyder}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </section>
        </div>

        {/* ── Fleet column (1/3) ── */}
        <div className="space-y-4">
          <SectionHeader dot="bg-teal-500" label="Mis domiciliarios" />

          {/* Fleet summary pills */}
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { label: "Libres",       count: disponibles, dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-100" },
              { label: "En pedido", count: ocupados,    dot: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-100"   },
              { label: "Inactivos", count: offline,     dot: "bg-gray-300",    text: "text-gray-500",    bg: "bg-gray-50",     border: "border-gray-100"    },
            ].map(({ label, count, dot, text, bg, border }) => (
              <div key={label} className={`${bg} border ${border} rounded-xl py-2.5 px-2 text-center`}>
                <div className="flex justify-center mb-1">
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                </div>
                <p className={`text-base font-black leading-none ${text}`}>{count}</p>
                <p className={`text-[8px] font-semibold mt-0.5 ${text}`}>{label}</p>
              </div>
            ))}
          </div>

          {/* Ryder list */}
          {ryders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 bg-white rounded-2xl border border-gray-100 text-center">
              <Truck size={28} className="mb-2 text-gray-200" />
              <p className="text-sm font-semibold text-gray-400">Sin domiciliarios</p>
              <p className="text-xs text-gray-300 mt-0.5">Aprueba solicitudes en la sección de domiciliarios</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {[
                ...ryders.filter(r => r.status === "disponible"),
                ...ryders.filter(r => r.status === "ocupado"),
                ...ryders.filter(r => r.status === "offline"),
              ].map(r => (
                <FleetRyderRow key={r.id} ryder={r} orders={orders} onView={setProfileRyder} />
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">¿Qué significa cada color?</p>
            <div className="space-y-1.5">
              {Object.entries(RYDER_CFG).map(([, v]) => (
                <div key={v.label} className="flex items-center gap-2 text-[10px] text-gray-500">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${v.dot}`} />
                  {v.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ryder profile modal (con mapa en tiempo real) */}
      {profileRyder && (
        <RyderProfileModal
          ryder={profileRyder}
          orders={orders}
          onClose={() => setProfileRyder(null)}
        />
      )}

      {/* Help modal */}
      <AnimatePresence>
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      </AnimatePresence>
    </div>
  );
}
